import { useRef } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { chapters } from '../data/portfolio';
import { EASE } from './Reveal';
import { scrollToId } from './scroll';

function ChapterPanel({ c, i, active }) {
    const left = i % 2 === 0;
    return (
        <div id={`chapter-${c.id}`} data-testid={`chapter-${c.id}`} className="relative flex min-h-[100svh] items-center py-14">
            <div className={`flex w-full px-6 md:px-10 ${left ? 'md:justify-start' : 'md:justify-end'}`}>
                <motion.article
                    initial={{ opacity: 0, y: 48 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className={`glass w-full max-w-xl rounded-2xl p-7 md:p-9 ${active ? 'ring-1' : ''}`}
                    style={active ? { boxShadow: `0 0 60px -18px ${c.accent}55`, borderColor: `${c.accent}44` } : {}}
                >
                    <div className="flex items-baseline justify-between gap-4">
                        <span className="font-display text-5xl font-extrabold text-outline md:text-6xl">
                            {c.stage}
                        </span>
                        <span
                            className="rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
                            style={{ borderColor: `${c.accent}55`, color: c.accent }}
                        >
                            {c.yearLabel}
                        </span>
                    </div>

                    <h3 className="mt-5 font-display text-2xl font-bold leading-tight text-snow md:text-4xl">
                        {c.title}
                    </h3>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist/70">
                        Node · {c.building}
                    </p>

                    <p className="mt-5 text-sm leading-relaxed text-mist md:text-[15px]">
                        {c.description}
                    </p>

                    {c.extras && (
                        <div className="mt-5 space-y-3.5 border-l pl-4" style={{ borderColor: `${c.accent}44` }} data-testid={`chapter-extras-${c.id}`}>
                            {c.extras.map((x) => (
                                <div key={x.label}>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: c.accent }}>
                                        {x.label}
                                    </p>
                                    <p className="mt-1 text-[13px] leading-relaxed text-mist/90">
                                        {x.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-6 flex flex-wrap gap-2">
                        {c.tags.map((t) => (
                            <span
                                key={t}
                                className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-snow/80"
                            >
                                {t}
                            </span>
                        ))}
                    </div>

                    {c.link && (
                        <a
                            data-testid={`chapter-link-${c.id}`}
                            href={c.link.href}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] transition-all hover:opacity-80"
                            style={{ border: `1px solid ${c.accent}55`, color: c.accent }}
                        >
                            {c.link.label} ↗
                        </a>
                    )}

                    {c.isPeak && c.peakNote && (
                        <p className="mt-6 border-t border-white/10 pt-4 font-mono text-[11px] leading-relaxed text-gold">
                            {c.peakNote}
                        </p>
                    )}
                </motion.article>
            </div>
        </div>
    );
}

export default function Journey({ worldRef, activeChapter, setActiveChapter }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

    useMotionValueEvent(scrollYProgress, 'change', (v) => {
        worldRef.current.p = v;
        const f = Math.min(Math.max(v * chapters.length - 0.5, 0), chapters.length - 1);
        setActiveChapter(Math.round(f));
    });

    return (
        <section id="journey" data-testid="journey-section" ref={ref} className="relative z-10">
            <div className="mx-auto flex max-w-[1500px]">
                <aside className="sticky top-0 hidden h-screen w-24 shrink-0 flex-col items-center justify-center self-start xl:flex" data-testid="journey-rail">
                    <span className="mb-5 font-mono text-[9px] tracking-[0.3em] text-mist/60">PATH</span>
                    <div className="relative h-56 w-px bg-white/10">
                        <motion.div
                            className="absolute inset-0 origin-top bg-neon"
                            style={{ scaleY: scrollYProgress }}
                        />
                    </div>
                    <div className="mt-5 flex flex-col gap-2.5">
                        {chapters.map((c, i) => (
                            <button
                                key={c.id}
                                data-testid={`rail-dot-${c.stage}`}
                                onClick={() => scrollToId(`chapter-${c.id}`)}
                                className={`font-mono text-[10px] tracking-widest transition-colors ${activeChapter === i ? 'text-neon' : 'text-mist/40 hover:text-mist'}`}
                            >
                                {c.stage}
                            </button>
                        ))}
                    </div>
                </aside>

                <div className="flex-1">
                    {chapters.map((c, i) => (
                        <ChapterPanel key={c.id} c={c} i={i} active={activeChapter === i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
